import { ZodSchema } from 'zod'
import { SentryMessageFunction } from '@grantstreet/sentry'

// The validation prefix to match on for failing automated testing.
// Due to the error listed in test-wrapper.ts, this value cannot
// be imported in the monorepo. If this message is changed, it will need
// to be changed in the monorepo as well.
export const validationErrorPrefix = 'Schema Validation Failure: '

/**
 * Wraps around a function and validates a function's passed in arguments
 * against Zod schema.
 * @param originalFunction - The original function that should have
 * its arguments validated.
 * @param strictArgumentsLength - If true, this will check if the
 * number of schema objects matches the number of passed in arguments in
 * the original function.
 * @param schemaObjects - Zod schema objects that will be validated
 * against the original function's passed in arguments.
 * @param exceptionLogger - An optional sentry message function
 * to log the error to
 */
export function withValidator<T extends unknown[]> (
  originalFunction: (...args: T) => unknown,
  strictArgumentsLength: boolean,
  schemaObjects: ZodSchema[],
  isInboundFunction: boolean,
  exceptionLogger?: SentryMessageFunction,
) {
  return function wrappedFunction (...args: Parameters<typeof originalFunction>) {
    parseSchema(strictArgumentsLength, schemaObjects, originalFunction.name, args, isInboundFunction, exceptionLogger)
    return originalFunction(...args)
  }
}

/**
 * Wraps around a Promise and validates a Promise's passed in arguments
 * against Zod schema.
 * @param originalAsyncFunction - The original function that should have
 * its arguments validated.
 * @param strictArgumentsLength - If true, this will check if the
 * number of schema objects matches the number of passed in arguments in
 * the original function.
 * @param schemaObjects - Zod schema objects that will be validated
 * against the original function's passed in arguments.
 * @param exceptionLogger - An optional sentry message function
 * to log the error to
 */
export function asyncWithValidator<T extends unknown[], U> (
  originalAsyncFunction: (...args: T) => Promise<U>,
  strictArgumentsLength: boolean,
  schemaObjects: ZodSchema[],
  isInboundFunction: boolean,
  exceptionLogger?: SentryMessageFunction,
) {
  return async function wrappedAsyncFunction (...args: Parameters<typeof originalAsyncFunction>) {
    parseSchema(strictArgumentsLength, schemaObjects, originalAsyncFunction.name, args, isInboundFunction, exceptionLogger)
    return await originalAsyncFunction(...args)
  }
}

/**
 * Helper function that validates passed in arguments against Zod schema.
 */
function parseSchema (
  strictArgumentsLength : boolean,
  schemaObjects: ZodSchema[],
  functionName: string,
  args: unknown[],
  isInboundFunction: boolean,
  exceptionLogger?: SentryMessageFunction,
) {
  // If no schemas are passed in, there's no point in validating.
  if (!schemaObjects.length) {
    const errorMessage = validationErrorPrefix + `At least one schema must be passed into the wrapper for function '${functionName}'`
    handleSchemaError(errorMessage, isInboundFunction, exceptionLogger)
    return
  }
  let optionalSchemaFound = false
  let requiredSchemasCount = 0
  for (let index = 0; index < schemaObjects.length; index++) {
    if (schemaObjects[index].isOptional()) {
      optionalSchemaFound = true
    }
    // In TypeScript, required parameters cannot follow optional parameters.
    // We want to prevent the same thing for schemas.
    else if (optionalSchemaFound) {
      const errorMessage = validationErrorPrefix + `Required schemas cannot follow optional schemas for function '${functionName}'`
      handleSchemaError(errorMessage, isInboundFunction, exceptionLogger)
      return
    }
    else {
      requiredSchemasCount++
    }
  }
  if (requiredSchemasCount > args.length) {
    // This means that the user didn't pass in enough arguments to validate against.
    const errorMessage = validationErrorPrefix + `There are more required schemas than passed in arguments for function '${functionName}'`
    handleSchemaError(errorMessage, isInboundFunction, exceptionLogger)
    return
  }
  if (strictArgumentsLength && requiredSchemasCount !== args.length) {
    const errorMessage = validationErrorPrefix + `Required schema objects length must equal passed in args length for function '${functionName}'`
    handleSchemaError(errorMessage, isInboundFunction, exceptionLogger)
    return
  }
  for (let index = 0; index < args.length; index++) {
    // If there are more arguments than schemas, we assume that we should
    // reuse the last schema to validate the argumnt against.
    const schemaPosition = Math.min(index, schemaObjects.length - 1)
    const res = schemaObjects[schemaPosition].safeParse(args[index])
    if (!res.success) {
      let errorMessage = validationErrorPrefix + `Schema validation failed at index ${index} with the following issues: `
      res.error.issues.forEach((zodIssue) => {
        let pathString = zodIssue.path[0]
        for (let i = 1; i < zodIssue.path.length; i++) {
          pathString += `[${zodIssue.path[i]}]`
        }

        errorMessage += `Zod Error Code: ${zodIssue.code}\nPath: ${pathString}\nMessage: ${zodIssue.message}\n`
      })
      handleSchemaError(errorMessage, isInboundFunction, exceptionLogger)
    }
  }
}

function handleSchemaError (errorMessage: string, isInboundFunction: boolean, exceptionLogger?: SentryMessageFunction) {
  console.error(errorMessage)
  if (exceptionLogger) {
    exceptionLogger(new Error(errorMessage))
  }
  if (isInboundFunction) {
    throw new Error(errorMessage)
  }
}
